import React from 'react';
import {
  Box,
  Typography,
  Button,
  Container,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
} from '@mui/material';
import {
  CheckCircle,
  CloudUpload,
  Assessment,
  Gavel,
  ArrowForward,
  Engineering,
  LocationCity,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import UserMenu from '../components/UserMenu';

const workflowSteps = [
  {
    title: 'Create Project',
    description: 'Set up a new surveying project with ordinance compliance tracking',
    features: [
      'Project organization',
      'Ordinance categorization',
      'Team collaboration setup'
    ],
    icon: <Engineering sx={{ fontSize: 40, color: 'primary.main' }} />,
    color: 'primary.light',
  },
  {
    title: 'Upload Documents',
    description: 'Secure cloud storage for all ordinance and compliance documents',
    features: [
      'PDF & Word support',
      'Secure S3 storage',
      'Version tracking'
    ],
    icon: <CloudUpload sx={{ fontSize: 40, color: 'secondary.main' }} />,
    color: 'secondary.light',
  },
  {
    title: 'Track Compliance',
    description: 'Monitor requirements and ensure full ordinance compliance',
    features: [
      'Requirement tracking',
      'Compliance scoring',
      'Progress monitoring'
    ],
    icon: <Assessment sx={{ fontSize: 40, color: 'success.main' }} />,
    color: 'success.light',
  },
];

export default function LandingPage() {
  const navigate = useNavigate();

  const handleStartProject = () => {
    navigate('/create-project');
  };

  const handleViewProjects = () => {
    navigate('/projects');
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50' }}>
      {/* Header */}
      <Box sx={{ bgcolor: 'white', boxShadow: 1 }}>
        <Container maxWidth="lg">
          <Box sx={{ py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocationCity sx={{ color: 'primary.main', fontSize: 28 }} />
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    bgcolor: 'secondary.main',
                    borderRadius: '50%',
                  }}
                />
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    bgcolor: 'primary.main',
                    borderRadius: '2px',
                    transform: 'rotate(45deg)',
                  }}
                />
              </Box>
              <Typography variant="h6" sx={{ ml: 2, fontWeight: 700, color: 'primary.main' }}>
                CivilSteve
              </Typography>
              <Typography variant="body2" sx={{ ml: 1, color: 'text.secondary', fontWeight: 500 }}>
                Surveying Compliance
            </Typography>
            </Box>

            {/* User Menu */}
            <UserMenu />
          </Box>
        </Container>
      </Box>

      {/* Hero Section */}
      <Container maxWidth="lg">
        <Grid container spacing={4} sx={{ py: 8 }}>
          <Grid item xs={12} md={6}>
            <Box sx={{ pt: 4 }}>
              <Typography
                variant="h2"
                component="h1"
                sx={{
                  fontWeight: 700,
                  mb: 2,
                  fontSize: { xs: '2.5rem', md: '3.5rem' },
                  lineHeight: 1.2,
                }}
              >
                Surveying Compliance{' '}
                <Typography
                  component="span"
                  variant="inherit"
                  sx={{ color: 'secondary.main' }}
                >
                  Made Simple.
                </Typography>
              </Typography>

              <Typography
                variant="h6"
                color="text.secondary"
                sx={{ mb: 4, fontWeight: 400, lineHeight: 1.6 }}
              >
                Streamline your surveying projects with intelligent ordinance compliance tracking
              </Typography>

              <Box sx={{ display: 'flex', gap: 2, mb: 4 }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleStartProject}
                  sx={{
                    px: 4,
                    py: 1.5,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '1.1rem',
                    boxShadow: 3,
                  }}
                >
                  Start New Project
                </Button>

                <Button
                  variant="outlined"
                  size="large"
                  onClick={handleViewProjects}
                  sx={{
                    px: 4,
                    py: 1.5,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '1.1rem',
                    borderWidth: 2,
                    '&:hover': {
                      borderWidth: 2,
                    }
                  }}
                >
                  View Projects
                </Button>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', justifyContent: 'center', pt: 2 }}>
              <Paper
                elevation={8}
                sx={{
                  p: 3,
                  borderRadius: 3,
                  transform: 'rotate(2deg)',
                  bgcolor: 'white',
                  maxWidth: 320,
                  width: '100%',
                  border: '2px solid',
                  borderColor: 'primary.main',
                }}
              >
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Box sx={{ width: 6, height: 6, bgcolor: 'success.main', borderRadius: '50%' }} />
                    <Typography variant="caption" color="text.secondary">
                      Project-2024-047
                    </Typography>
                    <Typography variant="caption" sx={{ ml: 'auto', fontWeight: 600 }}>
                      85%
                    </Typography>
                  </Box>

                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                    Downtown Subdivision
                  </Typography>
                </Box>

                <List dense>
                  <ListItem sx={{ pl: 0 }}>
                    <ListItemIcon sx={{ minWidth: 24 }}>
                      <CheckCircle sx={{ fontSize: 16, color: 'success.main' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="body2">
                          Zoning Ordinance Review
                        </Typography>
                      }
                      secondary={
                        <Typography variant="caption" color="text.secondary">
                          Compliant - Complete
                        </Typography>
                      }
                    />
                  </ListItem>

                  <ListItem sx={{ pl: 0 }}>
                    <ListItemIcon sx={{ minWidth: 24 }}>
                      <Box sx={{ width: 4, height: 4, bgcolor: 'warning.main', borderRadius: '50%', ml: 0.75 }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="body2">
                          Subdivision Requirements
                        </Typography>
                      }
                      secondary={
                        <Typography variant="caption" color="text.secondary">
                          In Progress - 70%
                        </Typography>
                      }
                    />
                  </ListItem>

                  <ListItem sx={{ pl: 0 }}>
                    <ListItemIcon sx={{ minWidth: 24 }}>
                      <CheckCircle sx={{ fontSize: 16, color: 'success.main' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="body2">
                          Environmental Impact
                        </Typography>
                      }
                      secondary={
                        <Typography variant="caption" color="text.secondary">
                          Compliant - Complete
                        </Typography>
                      }
                    />
                  </ListItem>

                  <ListItem sx={{ pl: 0 }}>
                    <ListItemIcon sx={{ minWidth: 24 }}>
                      <Gavel sx={{ fontSize: 16, color: 'secondary.main' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="body2">
                          Building Code Review
                        </Typography>
                      }
                      secondary={
                        <Typography variant="caption" color="text.secondary">
                          Pending Review
                        </Typography>
                      }
                    />
                  </ListItem>
                </List>

                <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                  Compliance Tracking Active
                </Typography>
              </Paper>
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* Features Section */}
      <Box sx={{ bgcolor: 'white', py: 8 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h3" component="h2" sx={{ fontWeight: 700, mb: 2 }}>
              Streamlined Workflow
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
              Manage surveying projects from start to finish with confidence and compliance
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {workflowSteps.map((step, index) => (
              <Grid item xs={12} md={4} key={step.title}>
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    border: 2,
                    borderColor: 'grey.200',
                    borderRadius: 3,
                    position: 'relative',
                    '&:hover': {
                      boxShadow: 6,
                      transform: 'translateY(-4px)',
                      transition: 'all 0.3s ease',
                      borderColor: 'primary.main',
                    },
                  }}
                >
                  <CardContent sx={{ p: 4, textAlign: 'center' }}>
                    <Box sx={{ mb: 3 }}>
                      {step.icon}
                    </Box>

                    <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
                      {step.title}
                    </Typography>

                    <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                      {step.description}
                    </Typography>

                    <List dense>
                      {step.features.map((feature, featureIndex) => (
                        <ListItem key={featureIndex} sx={{ justifyContent: 'center', py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 24 }}>
                            <CheckCircle sx={{ fontSize: 16, color: 'success.main' }} />
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography variant="body2" color="text.secondary">
                                {feature}
                              </Typography>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>

                  {/* Arrow for flow indication */}
                  {index < workflowSteps.length - 1 && (
                    <Box
                      sx={{
                        position: 'absolute',
                        right: -20,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        zIndex: 1,
                        display: { xs: 'none', md: 'block' },
                        bgcolor: 'white',
                        borderRadius: '50%',
                        p: 1,
                        boxShadow: 2,
                      }}
                    >
                      <ArrowForward sx={{ color: 'secondary.main', fontSize: 24 }} />
                    </Box>
                  )}
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Footer */}
      <Box sx={{ bgcolor: 'grey.100', py: 6 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 2 }}>
              <LocationCity sx={{ color: 'primary.main', fontSize: 24 }} />
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
                CivilSteve
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              © 2024 CivilSteve - Professional Surveying Compliance Management
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}